import { FC, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDidMount } from "rooks";
import { TabPanel } from "@mui/lab";
import { Box } from "@mui/material";
import moment from "moment";
import { Interval } from "../../../../common/create/scheduler/Interval";
import {
  DateTimeContent,
  ScheduleDatesInterface,
} from "../../../../common/create/scheduler/DateTimeContent";
import { NextPrevious } from "../../../../common/create/NextPrevious";
import { startEndDatesValidation } from "../../../../common/utils/constants/validation";
import { RunAt } from "../RunAt";
import {
  WorkflowSchedulerData,
  WorkflowSchedulerDateChangeType,
} from "../../../utils/types/types";
import { parseCron } from "../../../../common/utils/helpers/cron/parseCron";
import { generateDailyCron } from "../../../../common/utils/helpers/cron/generateDailyCron";

interface InitialValuesInterface {
  interval: number;
  runAt: string;
}

const initialValues: InitialValuesInterface & ScheduleDatesInterface = {
  runAt: moment().set({ hour: 0, minute: 0 }).format(),
  interval: 0,
  startDate: "",
  endDate: "",
  timeZone: "",
  id: "",
};

const validationSchema = Yup.object({
  interval: Yup.number().required().min(1, "Please select interval"),
  startDate: startEndDatesValidation,
  endDate: startEndDatesValidation,
});

interface DailyProps {
  cron: string | null;
  setScheduleDatesData(data: WorkflowSchedulerData): void;
  onDateChange(data: WorkflowSchedulerDateChangeType): void;
  onPreviousStep(): void;
  hideCancelButton?: boolean;
  isDirty: boolean;
}

export const Daily: FC<DailyProps & ScheduleDatesInterface> = ({
  cron,
  startDate,
  setScheduleDatesData,
  endDate,
  timeZone,
  onPreviousStep,
  id,
  isDirty,
  hideCancelButton,
  onDateChange,
}) => {
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      const cron = generateDailyCron(values.interval, values.runAt);

      setScheduleDatesData({
        startDate: values.startDate,
        endDate: values.endDate,
        cron,
        timeZone: values.timeZone,
        dailyScheduleInterval: values.interval,
      });
    },
  });

  const {
    values,
    errors,
    touched,
    submitForm,
    setFieldValue,
    resetForm,
    dirty,
    isValid,
    setValues,
  } = formik;

  useDidMount(() => {
    const { daysOfMonth, minutes, hours } = parseCron(cron);
    const minute = minutes || 0;
    const hour = hours || 0;
    const runAt = moment().set({ hour, minute }).format();
    const interval = daysOfMonth ?? 1;
    const values = {
      runAt,
      interval,
      startDate,
      endDate,
      timeZone,
      id,
    };
    onDateChange({
      endDate,
      timeZone,
      interval,
      startDate: startDate ?? "",
      cron: generateDailyCron(interval, runAt),
    });

    if (id) {
      resetForm({ values });
    } else {
      setValues(values);
    }
  });

  const dateChangeHandler = useCallback(
    (key: string, value: number | string) => {
      const updatedValues = {
        ...values,
        [key]: value,
      };
      const { startDate, interval, runAt, endDate, timeZone } = updatedValues;

      onDateChange({
        endDate,
        timeZone,
        interval,
        startDate: startDate ?? "",
        cron: generateDailyCron(interval, runAt),
      });
    },
    [values, onDateChange],
  );

  const setFieldValueHandler = useCallback(
    (key: string, value: number | string) => {
      setFieldValue(key, value);
      dateChangeHandler(key, value);
    },
    [setFieldValue, dateChangeHandler],
  );

  return (
    <TabPanel value="daily">
      <form>
        <Interval
          type="Days"
          value={values.interval}
          error={touched.interval && errors.interval}
          setFieldValue={setFieldValueHandler}
        />
        <RunAt value={values.runAt} setFieldValue={setFieldValueHandler} />

        <DateTimeContent
          endDate={values.endDate}
          startDate={values.startDate}
          timeZone={values.timeZone}
          endDateError={touched.endDate && errors.endDate}
          startDateError={touched.startDate && errors.startDate}
          setFieldValue={setFieldValueHandler}
          showNextRuns
          tabType="daily"
        />
        <Box mt={4}>
          <NextPrevious
            hideCancelButton={hideCancelButton}
            onNext={submitForm}
            onPrevious={onPreviousStep}
            isDirty={dirty || isDirty}
            isNextDisabled={!isValid}
          />
        </Box>
      </form>
    </TabPanel>
  );
};
